import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import MonitoringSourceRow from "@/components/customers/MonitoringSourceRow";
import SectionCard from "@/components/customers/SectionCard";
import { useMonitoringSources } from "@/hooks/use-monitoring-sources";
import { formatNOK, formatRelativeTime } from "@/lib/format";
import { FREQUENCY_LABELS, MONITORING_SOURCES, monitoringMode, monitoringRunStatus, type MonitoringRunStatus } from "@/lib/monitoring-sources";
import { signalSourceChip } from "@/lib/signal-types";
import type { DBCustomerMonitoringSource } from "@/lib/db-types";

/** Én lesbar kilderad: kildemerke, navn, adresse · frekvens · sist kjørt, og driftsstatus. */
function SourceLine({ source, label, detail, status }: { source: string; label: string; detail: string; status: MonitoringRunStatus | null }) {
  return (
    <li className="flex items-center gap-3 py-2 text-sm">
      <span className="inline-flex h-6 w-9 shrink-0 items-center justify-center bg-muted text-[10px] font-semibold tracking-wide" aria-hidden="true">
        {signalSourceChip(source)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{label}</span>
        <span className="block truncate text-xs text-muted-foreground">{detail}</span>
      </span>
      {status && <StatusBadge tone={status.tone} label={status.label} />}
    </li>
  );
}

function detailFor(row: DBCustomerMonitoringSource | undefined): string {
  if (!row?.identifier) return "ingen adresse";
  const parts = [
    row.identifier.replace(/^https?:\/\//, "").replace(/\/$/, ""),
    FREQUENCY_LABELS[row.frequency].toLowerCase(),
    row.last_run_at ? `sist ${formatRelativeTime(row.last_run_at)}` : "ikke kjørt ennå",
    monitoringMode(row) === "overridden" ? "overstyrt" : "følger status",
  ];
  return parts.join(" · ");
}

/**
 * Overvåkning per kilde (CRM-3.2): lesbare rader med adresse, frekvens, siste
 * kjøring og driftsstatus, Brønnøysund som alltid-på-rad, og samlet kostnad.
 * Brytere, adresser og frekvens redigeres i dialogen «Rediger kilder», så
 * kundekortet leser som et kort og ikke som innstillinger. Bak feature_crm_signaler.
 */
export default function MonitoringSection({ customerId }: { customerId: string }) {
  const { data: rows = [], isLoading } = useMonitoringSources(customerId);
  const [editOpen, setEditOpen] = useState(false);
  const activeCount = rows.filter((r) => r.is_active).length;
  const costTotal = rows.reduce((s, r) => s + Number(r.cost_total_nok ?? 0), 0);

  return (
    <>
      <SectionCard
        compact
        title="Overvåkning"
        meta={isLoading ? undefined : activeCount > 0 ? `${activeCount} ${activeCount === 1 ? "kilde" : "kilder"} på` : "bare Brønnøysund"}
      >
        {isLoading ? (
          <Skeleton className="m-4 h-24" />
        ) : (
          <div className="px-4 pb-3">
            <ul className="divide-y divide-border">
              <SourceLine source="brreg" label="Brønnøysund" detail="alltid på · gratis" status={{ tone: "reached", label: "OK" }} />
              {MONITORING_SOURCES.map((meta) => {
                const row = rows.find((r) => r.source === meta.kind);
                return <SourceLine key={meta.kind} source={meta.kind} label={meta.label} detail={detailFor(row)} status={monitoringRunStatus(row)} />;
              })}
            </ul>
            <div className="flex items-center justify-between gap-2 border-t border-border pt-2 text-xs text-muted-foreground">
              <span>Kostnad hittil {formatNOK(costTotal)}</span>
              <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setEditOpen(true)}>
                Rediger kilder
              </Button>
            </div>
          </div>
        )}
      </SectionCard>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Overvåkningskilder</DialogTitle>
            <DialogDescription>
              Kildene sjekkes etter frekvensen sin. Nye funn havner i «Hva skjer hos dem» og kan utløse forslag i ukeslisten.
              Overvåkning følger kundestatus (varm og medium er på), og du kan overstyre per kilde. Brønnøysund er alltid på.
            </DialogDescription>
          </DialogHeader>
          <ul>
            {MONITORING_SOURCES.map((meta) => (
              <MonitoringSourceRow key={meta.kind} customerId={customerId} meta={meta} row={rows.find((r) => r.source === meta.kind)} />
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
